<template>
  <div class="meeting-booking-container">
    <div class="container">
      <div class="text-center mt-4" v-if="isLoading">
        <HMSpinner />
      </div>
      <div v-else-if="meetingType">
        <div class="row mt-5" v-if="!showQuestionsForm">
          <div class="col-12 text-center">
            <HMInstructorsStack
              :instructors="meetingType.instructors"
              :size="'65px'"
              :clickable="false"
            />
          </div>
          <div class="col-12 offset-md-3 col-md-6 mt-4 text-center">
            <h2>
              {{ meetingType.name }} {{ $t('MARKETING.LABELS.WITH') }}
              {{ instructorNames }}
            </h2>
            <HMVueEditorPreview
              v-if="!showQuestionsForm && meetingType.description"
              :html="meetingType.description"
              class="mt-3"
            />
          </div>
        </div>
        <div class="row mt-5" v-else>
          <div
            class="col-12 d-flex flex-row flex-nowrap justify-content-between align-items-center"
          >
            <h1>{{ meetingType.name }}</h1>
            <div class="d-flex flex-row justify-content-end align-items-center">
              <HMInstructorsStack
                :instructors="meetingType.instructors"
                :size="'30px'"
                :clickable="false"
              />
              <span class="c-charcoal ml-3">{{ instructorNames }}</span>
            </div>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12" v-if="!showQuestionsForm">
            <PublicSchedulingCalendar
              class="transition-card"
              :studioURL="studioURL"
              :meetingType="meetingType"
              @selectedSlot="selectSlot"
            />
          </div>
          <div class="col-12 offset-md-2 col-md-8" v-else>
            <div class="mb-3 h4">
              {{
                $t(
                  'MARKETING.TEXT.PLEASE_ANSWER_THESE_QUESTIONS_TO_COMPLETE_YOUR_BOOKING'
                )
              }}
            </div>
            <ValidationObserver
              @submit.prevent="book"
              tag="form"
              ref="form"
              v-slot="{ invalid }"
            >
              <label>{{ $t('MARKETING.LABELS.FULL_NAME') }}</label>
              <HMTextInput
                type="text"
                rules="required|max:128"
                name="Full name"
                v-model="bookingFullName"
              />
              <label>{{ $t('MARKETING.LABELS.EMAIL_ADDRESS') }}</label>
              <HMTextInput
                type="text"
                rules="required|email|max:128"
                name="email address"
                v-model="bookingEmail"
              />
              <div
                class="form-group"
                v-for="question in questions"
                :key="question.position"
              >
                <label>{{ question.label }}</label>
                <HMTextInput
                  type="text"
                  :rules="question.required ? 'required' : ''"
                  name="question"
                  v-model="question.answer"
                />
              </div>
              <div
                class="d-flex flex-row justify-content-between align-items-center mt-4"
              >
                <div @click="showQuestionsForm = false" class="pointer">
                  <i class="fa fa-chevron-left mr-2"></i>
                  {{ $t('MARKETING.LABELS.BACK_TO_CALENDAR') }}
                </div>
                <HMButton
                  color="brand"
                  :disabled="invalid || isBooking"
                  type="submit"
                  :loading="isBooking"
                >
                  {{ $t('MARKETING.BUTTONS.COMPLETE_BOOKING') }}
                </HMButton>
              </div>
            </ValidationObserver>
          </div>
          <div class="col-12 my-5"></div>
        </div>
      </div>
      <div v-else class="text-center mt-4">
        <h1>{{ $t('MARKETING.TEXT.THIS_MEETING_TYPE_DOES_NOT_EXIST') }}</h1>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import Vue from 'vue';
import { getStudioURL } from '../../router/custom-domain-utils';
import PublicSchedulingCalendar from '../../components/products/PublicSchedulingCalendar';
import HMButton from '../../components/common/HMButton';
import { ValidationObserver } from 'vee-validate';
import HMVueEditorPreview from '@/web/components/common/HMVueEditorPreview.vue';
import { metaTitles } from '../../router/meta-utils';
import { getCurrentUrl } from '../../utils/url';

export default Vue.extend({
  name: 'StudioMeetingBooking',
  components: {
    HMButton,
    PublicSchedulingCalendar,
    ValidationObserver,
    HMVueEditorPreview,
  },
  metaInfo() {
    return {
      title: this.metaTitle,
      meta: [
        ...metaTitles(this.metaTitle),
        {
          vmid: 'og:image',
          property: 'og:image',
          content:
            this.studio?.cover_photo?.storage_url ??
            this.studio?.cover_photo?.embedded_image_url,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: getCurrentUrl(),
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
    }),
    metaTitle() {
      if (!this.meetingType) return '';
      if (!this.meetingType.instructors.length) return this.meetingType.name;

      return `${this.meetingType.name} ${this.$t('MARKETING.LABELS.WITH')} ${
        this.instructorNames
      }`;
    },
    instructorNames() {
      return this.meetingType?.instructors.map(i => i.name).join(', ');
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    questions() {
      if (!this.meetingType?.form || !this.meetingType.form.questions)
        return [];
      return (
        [...this.meetingType.form.questions].sort(
          (p1, p2) => p1.position - p2.position
        ) ?? []
      );
    },
  },
  data() {
    return {
      isLoading: true,
      meetingType: null,
      showQuestionsForm: false,
      selectedSlot: null,
      timezone: null,
      inProgress: false,
      bookingFullName: null,
      bookingEmail: null,
      isBooking: false,
    };
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        this.meetingType = await this.$store.dispatch(
          'marketing/getPublicMeetingType',
          {
            studioURL: this.studioURL,
            slug: this.$route.params.meeting_type_slug,
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not load this meeting type',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    selectSlot({ selectedSlot, selectedTimezone }) {
      this.selectedSlot = selectedSlot;
      this.timezone = selectedTimezone;
      this.showQuestionsForm = true;
    },
    async book() {
      this.isBooking = true;
      try {
        const bookedMeeting = await this.$store.dispatch(
          'marketing/bookPublicMeetingSlot',
          {
            studioURL: this.studioURL,
            slug: this.meetingType.slug,
            slot: this.selectedSlot,
            timezone: this.timezone,
            bookingFullName: this.bookingFullName,
            bookingEmail: this.bookingEmail,
            form: this.questions,
          }
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PRODUCTS.NOTIFICATIONS.PRIVATE_SESSION_SUCCESSFULLY_BOOKED_ON',
            {
              slot: this.$formatDate(
                this.selectedSlot.toDate(),
                'date-time-long'
              ),
            }
          ),
        });
        this.$router.push({
          name: 'studio-booked-meeting',
          params: {
            studio_slug: this.studioURL,
            meeting_unique_id: bookedMeeting.unique_id,
          },
        });
      } catch (error) {
        console.log(error);
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to book your slot!',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isBooking = false;
      }
    },
  },
  async mounted() {
    await this.loadData();
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.meeting-booking-container {
}

/* Prev Scrolling */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Back Scrolling */
.slide-fade-reverse-enter-active,
.slide-fade-reverse-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-fade-reverse-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-fade-reverse-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.transition-card + .transition-card {
  position: absolute;
}

.transition-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
